// =============================================
// COMPONENTE: FlowSimulator
// =============================================
// Arquivo: FlowSimulator.tsx
// Descrição: Simulador de conversa para testar fluxos
// =============================================

import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, Play, RotateCcw, CheckCircle, XCircle, MessageSquare } from 'lucide-react';
import './FlowSimulator.css';

interface Message {
  id: string;
  type: 'user' | 'bot' | 'system';
  content: string;
  timestamp: Date;
  nodeId?: string;
}

interface FlowSimulatorProps {
  flowId: number;
  flowData: {
    nodes: any[];
    edges: any[];
  };
  onClose?: () => void;
}

const FlowSimulator: React.FC<FlowSimulatorProps> = ({ flowId, flowData, onClose }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [currentNodeId, setCurrentNodeId] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [executionStatus, setExecutionStatus] = useState<'idle' | 'running' | 'completed' | 'error'>('idle');
  const [context, setContext] = useState<Record<string, any>>({});

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // =============================================
  // AUTO SCROLL
  // =============================================

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // =============================================
  // INICIAR SIMULAÇÃO
  // =============================================

  const startSimulation = () => {
    setMessages([]);
    setContext({});
    setExecutionStatus('running');
    setIsRunning(true);

    // Encontrar nó start
    const startNode = flowData.nodes.find(node => node.type === 'start');

    if (!startNode) {
      addSystemMessage('Erro: Nó de início não encontrado');
      setExecutionStatus('error');
      setIsRunning(false);
      return;
    }

    setCurrentNodeId(startNode.id);
    addSystemMessage('Simulação iniciada');

    // Processar primeiro nó
    setTimeout(() => processNode(startNode.id), 500);
  };

  // =============================================
  // PROCESSAR NÓ
  // =============================================

  const processNode = (nodeId: string) => {
    const node = flowData.nodes.find(n => n.id === nodeId);

    if (!node) {
      addSystemMessage(`Erro: Nó ${nodeId} não encontrado`);
      setExecutionStatus('error');
      setIsRunning(false);
      return;
    }

    setCurrentNodeId(nodeId);

    switch (node.type) {
      case 'start':
        moveToNextNode(nodeId);
        break;

      case 'message':
        handleMessageNode(node);
        break;

      case 'input':
        handleInputNode(node);
        break;

      case 'condition':
        handleConditionNode(node);
        break;

      case 'action':
        handleActionNode(node);
        break;

      case 'webhook':
        handleWebhookNode(node);
        break;

      case 'delay':
        handleDelayNode(node);
        break;

      case 'database':
        handleDatabaseNode(node);
        break;

      case 'end':
        handleEndNode();
        break;

      default:
        addSystemMessage(`Tipo de nó desconhecido: ${node.type}`);
        moveToNextNode(nodeId);
    }
  };

  // =============================================
  // HANDLERS DE NÓS
  // =============================================

  const handleMessageNode = (node: any) => {
    const message = replaceVariables(node.data.message || node.data.label);
    addBotMessage(message, node.id);
    setTimeout(() => moveToNextNode(node.id), 1000);
  };

  const handleInputNode = (node: any) => {
    addBotMessage(`Por favor, envie: ${node.data.label}`, node.id);
    setIsRunning(false); // Aguardar input do usuário
  };

  const handleConditionNode = (node: any) => {
    const condition = node.data.condition || '';
    const result = evaluateCondition(condition);

    addSystemMessage(`Avaliando condição: ${condition} = ${result ? 'Verdadeiro' : 'Falso'}`);

    // Encontrar edge correspondente
    const edge = flowData.edges.find(e =>
      e.source === node.id &&
      (result ? e.label === 'Sim' : e.label === 'Não')
    );

    if (edge) {
      setTimeout(() => processNode(edge.target), 800);
    } else {
      addSystemMessage('Erro: Caminho não encontrado para a condição');
      setExecutionStatus('error');
      setIsRunning(false);
    }
  };

  const handleActionNode = (node: any) => {
    const actionType = node.data.actionType || 'unknown';
    addSystemMessage(`Executando ação: ${actionType}`, node.id);
    setTimeout(() => moveToNextNode(node.id), 1000);
  };

  const handleWebhookNode = (node: any) => {
    const url = node.data.url || 'http://example.com/api';
    addSystemMessage(`Chamando webhook: ${url}`, node.id);
    setTimeout(() => moveToNextNode(node.id), 1500);
  };

  const handleDelayNode = (node: any) => {
    const duration = node.data.duration || 5;
    const unit = node.data.unit || 'segundos';
    addSystemMessage(`Aguardando ${duration} ${unit}...`, node.id);

    // Simular delay (reduzido para teste)
    const delayMs = Math.min(duration * (unit === 'minutos' ? 60000 : 1000), 3000);
    setTimeout(() => moveToNextNode(node.id), delayMs);
  };

  const handleDatabaseNode = (node: any) => {
    const action = node.data.dbAction || 'select';
    const table = node.data.table || 'tabela';
    addSystemMessage(`${action.toUpperCase()} em ${table}`, node.id);
    setTimeout(() => moveToNextNode(node.id), 1000);
  };

  const handleEndNode = () => {
    addSystemMessage('Fluxo finalizado com sucesso');
    setExecutionStatus('completed');
    setIsRunning(false);
    setCurrentNodeId(null);
  };

  // =============================================
  // MOVER PARA PRÓXIMO NÓ
  // =============================================

  const moveToNextNode = (currentNodeId: string) => {
    const nextEdge = flowData.edges.find(e => e.source === currentNodeId);

    if (nextEdge) {
      setTimeout(() => processNode(nextEdge.target), 500);
    } else {
      addSystemMessage('Fim do fluxo (sem próximo nó)');
      setExecutionStatus('completed');
      setIsRunning(false);
      setCurrentNodeId(null);
    }
  };

  // =============================================
  // ENVIAR MENSAGEM DO USUÁRIO
  // =============================================

  const handleSendMessage = () => {
    if (!inputValue.trim() || !currentNodeId) return;

    const currentNode = flowData.nodes.find(n => n.id === currentNodeId);

    if (currentNode && currentNode.type === 'input') {
      const variableName = currentNode.data.variableName || 'input';

      // Adicionar mensagem do usuário
      addUserMessage(inputValue);

      // Salvar no contexto
      setContext(prev => ({
        ...prev,
        [variableName]: inputValue
      }));

      addSystemMessage(`Variável "${variableName}" salva: ${inputValue}`);

      setInputValue('');
      setIsRunning(true);

      // Continuar fluxo
      setTimeout(() => moveToNextNode(currentNodeId), 500);
    }
  };

  // =============================================
  // ADICIONAR MENSAGENS
  // =============================================

  const addUserMessage = (content: string) => {
    const message: Message = {
      id: Date.now().toString(),
      type: 'user',
      content,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, message]);
  };

  const addBotMessage = (content: string, nodeId?: string) => {
    const message: Message = {
      id: Date.now().toString(),
      type: 'bot',
      content,
      timestamp: new Date(),
      nodeId
    };
    setMessages(prev => [...prev, message]);
  };

  const addSystemMessage = (content: string, nodeId?: string) => {
    const message: Message = {
      id: Date.now().toString(),
      type: 'system',
      content,
      timestamp: new Date(),
      nodeId
    };
    setMessages(prev => [...prev, message]);
  };

  // =============================================
  // UTILITÁRIOS
  // =============================================

  const replaceVariables = (text: string): string => {
    let result = text;
    Object.keys(context).forEach(key => {
      result = result.replace(new RegExp(`{{${key}}}`, 'g'), context[key]);
    });
    return result;
  };

  const evaluateCondition = (condition: string): boolean => {
    try {
      // Substituir variáveis
      let evalCondition = condition;
      Object.keys(context).forEach(key => {
        const value = context[key];
        // Converter valor para string apropriada para comparação
        const replacement = typeof value === 'string' ? `"${value}"` : String(value);
        evalCondition = evalCondition.replace(new RegExp(`{{${key}}}`, 'g'), replacement);
      });

      // Parser seguro de condições (sem eval)
      return safeEvaluateExpression(evalCondition);
    } catch {
      return false;
    }
  };

  // Parser seguro de expressões booleanas
  const safeEvaluateExpression = (expr: string): boolean => {
    // Remover espaços extras
    expr = expr.trim();

    // Tratar operadores lógicos (|| e &&)
    if (expr.includes('||')) {
      const parts = expr.split('||');
      return parts.some(part => safeEvaluateExpression(part));
    }
    if (expr.includes('&&')) {
      const parts = expr.split('&&');
      return parts.every(part => safeEvaluateExpression(part));
    }

    // Tratar negação
    if (expr.startsWith('!')) {
      return !safeEvaluateExpression(expr.slice(1));
    }

    // Tratar parênteses
    if (expr.startsWith('(') && expr.endsWith(')')) {
      return safeEvaluateExpression(expr.slice(1, -1));
    }

    // Literais booleanos
    if (expr === 'true') return true;
    if (expr === 'false') return false;

    // Operadores de comparação
    const operators = ['===', '!==', '==', '!=', '>=', '<=', '>', '<'];
    for (const op of operators) {
      if (expr.includes(op)) {
        const [left, right] = expr.split(op).map(s => parseValue(s.trim()));
        switch (op) {
          case '===':
          case '==': return left === right;
          case '!==':
          case '!=': return left !== right;
          case '>=': return Number(left) >= Number(right);
          case '<=': return Number(left) <= Number(right);
          case '>': return Number(left) > Number(right);
          case '<': return Number(left) < Number(right);
        }
      }
    }

    // Se for um valor simples, verificar se é truthy
    const value = parseValue(expr);
    return Boolean(value);
  };

  // Parser de valores
  const parseValue = (val: string): string | number | boolean => {
    val = val.trim();
    // String entre aspas
    if ((val.startsWith('"') && val.endsWith('"')) || (val.startsWith("'") && val.endsWith("'"))) {
      return val.slice(1, -1);
    }
    // Booleanos
    if (val === 'true') return true;
    if (val === 'false') return false;
    if (val === 'null' || val === 'undefined') return '';
    // Números
    const num = Number(val);
    if (!isNaN(num)) return num;
    // Retornar como string
    return val;
  };

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // =============================================
  // RENDERIZAÇÃO
  // =============================================

  return (
    <div className="flow-simulator">
      {/* HEADER */}
      <div className="simulator-header">
        <div className="simulator-title">
          <MessageSquare size={20} />
          <h3>Simulador de Conversa</h3>
        </div>

        <div className="simulator-status">
          {executionStatus === 'running' && (
            <span className="status-badge running">
              <Play size={14} />
              Executando
            </span>
          )}
          {executionStatus === 'completed' && (
            <span className="status-badge completed">
              <CheckCircle size={14} />
              Completo
            </span>
          )}
          {executionStatus === 'error' && (
            <span className="status-badge error">
              <XCircle size={14} />
              Erro
            </span>
          )}
        </div>

        <div className="simulator-actions">
          <button
            onClick={startSimulation}
            className="btn-start"
            disabled={isRunning}
          >
            <RotateCcw size={16} />
            Reiniciar
          </button>
          {onClose && (
            <button onClick={onClose} className="btn-close">
              ×
            </button>
          )}
        </div>
      </div>

      {/* MESSAGES AREA */}
      <div className="simulator-messages">
        {messages.length === 0 ? (
          <div className="empty-state">
            <MessageSquare size={48} />
            <p>Clique em "Reiniciar" para iniciar a simulação</p>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <div
                key={message.id}
                className={`message message-${message.type}`}
              >
                <div className="message-content">
                  {message.content}
                </div>
                <div className="message-time">
                  {formatTime(message.timestamp)}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* INPUT AREA */}
      <div className="simulator-input">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
          placeholder={
            isRunning
              ? 'Aguardando resposta do bot...'
              : executionStatus === 'idle'
              ? 'Clique em "Reiniciar" para iniciar'
              : 'Digite sua mensagem...'
          }
          disabled={isRunning || executionStatus === 'idle' || executionStatus === 'completed'}
        />
        <button
          onClick={handleSendMessage}
          disabled={!inputValue.trim() || isRunning || executionStatus === 'idle'}
          className="btn-send"
        >
          <ArrowRight size={18} />
        </button>
      </div>

      {/* CONTEXT DISPLAY */}
      {Object.keys(context).length > 0 && (
        <div className="simulator-context">
          <strong>Variáveis capturadas:</strong>
          {Object.entries(context).map(([key, value]) => (
            <span key={key} className="context-item">
              {key}: {value}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default FlowSimulator;
